/**
 * Helpers — green-spa.pro Devis Ouverture
 * ============================================================
 * Fonctions pures (sans DOM, sans fetch) extraites pour pouvoir
 * être testées en Node et réutilisées côté front.
 *
 * Compatible browser (window.ProDevisHelpers) ET Node (CommonJS).
 * ============================================================
 */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) {
    module.exports = factory();
  } else {
    root.ProDevisHelpers = factory();
  }
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  /**
   * Compile une regex à partir d'une string (sans les slashs).
   *
   * Protections appliquées :
   *  • Source vide → null (pas de filtre)
   *  • Source trop longue (> 200 chars) → null (anti-ReDoS basique)
   *  • Source avec backref ou lookbehind exotiques → autorisée mais protégée
   *    par un test runtime sur un input long pour détecter le catastrophic
   *    backtracking. Si > 50 ms, on rejette.
   *  • Erreur de syntaxe → null
   *
   * Retourne null sur erreur ; le caller traite "null" comme "pas de filtre".
   */
  function compileWhitelistRegex(source) {
    if (!source || typeof source !== 'string') return null;
    if (source.length > 200) {
      console.warn('[prodevis] Regex trop longue (max 200 chars), ignorée.');
      return null;
    }
    try {
      const regex = new RegExp(source, 'i');
      // ReDoS canary : un titre potentiellement adversarial. Avec un input de
      // 20 chars, une regex avec catastrophic backtracking explose facilement
      // au-dessus de 30 ms ; une regex normale termine en < 1 ms.
      // Le canary est volontairement court pour ne PAS hanger l'init si
      // l'input est très long — on accepte de rater quelques cas subtils.
      const canary = 'a'.repeat(20) + '!';
      const t0 = (typeof performance !== 'undefined' ? performance : Date).now();
      regex.test(canary);
      const elapsed = (typeof performance !== 'undefined' ? performance : Date).now() - t0;
      if (elapsed > 30) {
        console.warn('[prodevis] Regex potentiellement catastrophique (ReDoS), ignorée.');
        return null;
      }
      return regex;
    } catch (e) {
      console.warn('[prodevis] Regex variant whitelist invalide :', source, e);
      return null;
    }
  }

  /**
   * Vérifie qu'une URL est sûre (http/https uniquement). Bloque les schemes
   * dangereux : javascript:, data:, vbscript:, file:, etc. Les URLs relatives
   * (commençant par /) sont autorisées car browser-safe.
   */
  function isSafeHttpUrl(url) {
    if (!url || typeof url !== 'string') return false;
    const trimmed = url.trim();
    if (trimmed.startsWith('/') && !trimmed.startsWith('//')) return true;
    try {
      const parsed = new URL(trimmed);
      return parsed.protocol === 'https:' || parsed.protocol === 'http:';
    } catch {
      return false;
    }
  }

  /**
   * Filtre les variantes d'un produit selon la whitelist.
   * Retourne un nouveau tableau (n'altère pas l'input).
   * Si regex null → toutes les variantes sont gardées.
   * Les variantes "Default Title" sont exclues (Shopify les utilise pour les
   * produits sans variantes ; elles ne devraient pas être proposées au pro).
   */
  function filterVariants(variants, regex) {
    if (!Array.isArray(variants)) return [];
    return variants.filter((v) => {
      if (!v || !v.available) return false;
      if (v.title === 'Default Title') return regex ? false : true;
      if (!regex) return true;
      return regex.test(String(v.title || ''));
    });
  }

  /**
   * Une variante porte-t-elle un FORMAT (contenance) ?
   *
   * Sert à décider si le filtre variantes d'une étape a un sens pour ce
   * produit : sans format (linge, actifs, accessoires, « Default Title »),
   * l'appelant affiche tout plutôt que d'écarter à tort des variantes.
   *
   * On exige un NOMBRE suivi de l'unité, jamais l'unité seule — sinon
   * « Taille L » passerait pour un format et le linge se ferait filtrer.
   * Les unités doivent rester alignées avec celles des
   * `variant_whitelist_regex` des sections (ml, L, g, kg) : une unité
   * reconnue ici mais absente de la whitelist ferait disparaître les
   * variantes concernées.
   */
  var FORMAT_VARIANT_RE = /\d\s*(ml|l|g|kg)\b/i;

  function hasFormatVariant(variants) {
    if (!Array.isArray(variants)) return false;
    return variants.some((v) => v && FORMAT_VARIANT_RE.test(String(v.title || '')));
  }

  /**
   * Parse la liste des exceptions au déblocage chaleur.
   *
   * Une par ligne, `<produit>|<variante>`, les deux côtés étant des regex
   * insensibles à la casse. Lignes vides et commentaires `#` ignorés.
   *
   * Le point-virgule sépare aussi : le même réglage existe côté boutique,
   * où le repli Liquid ne peut pas contenir de retour à la ligne. Les deux
   * parseurs acceptent donc les deux séparateurs — une ligne copiée d'une
   * config à l'autre doit se comporter pareil, sinon elle change de sens
   * en silence.
   *
   *   Orchid|^Revente 180 ml$
   *   Patchouli|^Revente 180 ml$
   *
   * Sémantique : CETTE variante de CE produit reste bloquée même si son
   * format fait partie des formats rouverts. Sert aux références dont le
   * beurre ne s'est pas resolidifié en refroidissant, alors que le même
   * format est redevenu vendable sur le reste de la gamme.
   *
   * Une ligne dont la regex ne compile pas est ignorée — elle ne doit pas
   * emporter tout le réglage avec elle.
   */
  function parseHeatExceptions(rawText) {
    const out = [];
    if (!rawText || typeof rawText !== 'string') return out;
    for (const line of rawText.split(/[\r\n;]/)) {
      const trimmed = line.trim();
      if (!trimmed || trimmed.startsWith('#')) continue;
      const sep = trimmed.indexOf('|');
      if (sep <= 0) continue;
      const product = trimmed.slice(0, sep).trim();
      const variant = trimmed.slice(sep + 1).trim();
      if (!product || !variant) continue;
      const p = compileWhitelistRegex(product);
      const v = compileWhitelistRegex(variant);
      if (p && v) out.push({ product: p, variant: v });
    }
    return out;
  }

  /**
   * Une variante est-elle bloquée à la vente par la règle chaleur ?
   *
   * Le blocage vise un PRODUIT (regex sur son titre, « beurre »), mais la
   * réouverture se fait par FORMAT : quand les températures baissent, on
   * ne remet pas toute la gamme d'un coup — les petits contenants
   * repartent avant les gros, qui mettent plus longtemps à refroidir.
   *
   * L'ordre de décision :
   *   1. pas de regex de blocage → rien n'est bloqué ;
   *   2. produit hors périmètre → pas bloqué ;
   *   3. produit visé, sans liste de formats rouverts → tout est bloqué
   *      (comportement historique, tout-ou-rien) ;
   *   4. format rouvert — soit par la regex de formats, soit par une
   *      réouverture ciblée `produit|variante` — SAUF si la paire
   *      produit/variante figure dans les exceptions.
   *
   * Les réouvertures ciblées servent aux produits dont les variantes ne
   * sont pas des formats : une trousse se décline par parfum, aucun
   * `Revente 180 ml` à matcher, alors qu'elle contient bien un beurre
   * 180 ml. On rouvre la trousse en bloc, puis on referme au parfum près
   * par une exception — la variante suit ainsi le beurre qu'elle contient.
   */
  function isHeatBlockedVariant(productTitle, variantTitle, opts) {
    const { blockRegex, allowRegex, allowPairs, exceptions } = opts || {};
    if (!blockRegex) return false;
    const pt = String(productTitle || '');
    if (!blockRegex.test(pt)) return false;
    const vt = String(variantTitle || '');
    let allowed = !!(allowRegex && allowRegex.test(vt));
    if (!allowed) {
      for (const pair of allowPairs || []) {
        if (pair && pair.product.test(pt) && pair.variant.test(vt)) { allowed = true; break; }
      }
    }
    if (!allowed) return true;
    for (const ex of exceptions || []) {
      if (ex && ex.product.test(pt) && ex.variant.test(vt)) return true;
    }
    return false;
  }

  /**
   * Vérifie qu'un produit a au moins un tag (case-insensitive) parmi
   * une liste fournie. Tolère les inputs malformés.
   */
  function hasAnyTag(productTags, wantedTags) {
    if (!Array.isArray(productTags) || !Array.isArray(wantedTags)) return false;
    const set = new Set(productTags.map((t) => String(t || '').trim().toLowerCase()));
    return wantedTags.some((t) => set.has(String(t || '').trim().toLowerCase()));
  }

  /**
   * Classifie l'état stock d'une variante en `in-stock` / `preorder` /
   * `out-of-stock`. Fonction pure, utilisée par le rendu du catalogue
   * réassort pour griser / labéliser / bloquer les variantes en rupture.
   *
   *  • `qty === undefined` ou `qty === null` → in-stock (pas de tracking
   *    Shopify, ou inventory non-fetché — on fait confiance à `available`
   *    qui aura déjà filtré les vraies indispos côté Shopify).
   *  • `qty > 0` → in-stock.
   *  • `qty <= 0` + `policy === 'CONTINUE'` → preorder (grisé, label
   *    "Précommande", achat autorisé). C'est le flag Shopify natif
   *    "Continuer à vendre en cas de rupture de stock" coché sur la
   *    variante dans l'admin.
   *  • `qty <= 0` + `policy === 'DENY'` (défaut Shopify) → out-of-stock
   *    (grisé, label "Rupture de stock", achat bloqué).
   */
  function classifyVariantStock(qty, policy) {
    if (qty === undefined || qty === null) return 'in-stock';
    if (qty > 0) return 'in-stock';
    return String(policy || '').toUpperCase() === 'CONTINUE' ? 'preorder' : 'out-of-stock';
  }

  /**
   * Parse le contenu textarea du mapping tag-produit → tag-client.
   *
   * Format (un mapping par ligne, lignes vides et commentaires `#` ignorés) :
   *
   *   valdys=thalassovaldys
   *   spadeslys=spadeslys
   *   # commentaire
   *   ANOTHER=tag1
   *
   * Sémantique : un produit qui porte le tag de gauche n'est visible
   * qu'aux clientes qui ont le tag de droite. Plusieurs tags clients par
   * tag produit autorisés via lignes multiples ; le set est alors traité
   * en OR (n'importe lequel suffit). Casse insensible des deux côtés.
   *
   * Retourne `Map<productTagLowercase, Set<customerTagLowercase>>`. Vide
   * pour entrée vide / non-string.
   */
  function parseCustomerTagMap(rawText) {
    const out = new Map();
    if (!rawText || typeof rawText !== 'string') return out;
    for (const line of rawText.split(/\r?\n/)) {
      const trimmed = line.trim();
      if (!trimmed || trimmed.startsWith('#')) continue;
      const eq = trimmed.indexOf('=');
      if (eq < 1) continue;
      const productTag = trimmed.slice(0, eq).trim().toLowerCase();
      const customerTag = trimmed.slice(eq + 1).trim().toLowerCase();
      if (!productTag || !customerTag) continue;
      let set = out.get(productTag);
      if (!set) {
        set = new Set();
        out.set(productTag, set);
      }
      set.add(customerTag);
    }
    return out;
  }

  /**
   * Gating par tag client : un produit dont les tags incluent `<prefix><X>`
   * (ex. `customer-only:thalassovaldys`) OU un tag mappé via
   * `productToCustomerMap` (ex. `valdys` → `thalassovaldys`) n'est visible
   * qu'aux clientes qui ont le tag client correspondant. Mirrore la logique
   * de l'app Locksmith côté front, pour les cas où Locksmith filtre les
   * pages produit mais pas l'endpoint `/collections/<handle>/products.json`
   * qu'on consomme.
   *
   * Deux mécanismes de gating, combinés en OR :
   *
   *  1. **Préfixe** (string `opts.prefix`, défaut `customer-only:`) — tout
   *     tag produit `<prefix><X>` exige que la cliente ait `<X>`.
   *  2. **Mapping explicite** (Map `opts.productToCustomerMap`) — tout
   *     tag produit listé en clé exige que la cliente ait l'un des tags
   *     de la valeur (Set). Permet d'utiliser des tags produit courts
   *     (`valdys`) sans le préfixe et de les relier à des tags client
   *     différents (`thalassovaldys`).
   *
   * Si AUCUN tag du produit ne déclenche un mécanisme → produit "public",
   * affiché à toutes. Sinon la cliente doit satisfaire AU MOINS UN des
   * tags requis (sémantique OR à la fois entre mécanismes et entre tags
   * d'un même produit).
   *
   * Pour rétro-compatibilité, `opts` peut aussi être une chaîne (traitée
   * comme `opts.prefix`).
   */
  function isProductCustomerEligible(productTags, customerTags, opts) {
    let prefix = '';
    let map = null;
    if (typeof opts === 'string') {
      prefix = opts;
    } else if (opts && typeof opts === 'object') {
      prefix = opts.prefix || '';
      map = opts.productToCustomerMap || null;
    }
    prefix = String(prefix).trim().toLowerCase();

    if (!Array.isArray(productTags)) return true;

    // Collecte tous les tags clients requis pour voir ce produit, à partir
    // des deux mécanismes (préfixe + mapping). Liste, pas Set — on conserve
    // les doublons éventuels, ça ne change pas la logique de matching.
    const requiredCustomerTags = [];
    const lowerProductTags = productTags
      .map((t) => String(t || '').trim().toLowerCase())
      .filter(Boolean);

    if (prefix) {
      for (const t of lowerProductTags) {
        if (t.startsWith(prefix)) {
          const required = t.slice(prefix.length).trim();
          if (required) requiredCustomerTags.push(required);
        }
      }
    }
    if (map && typeof map.get === 'function') {
      for (const t of lowerProductTags) {
        const mapped = map.get(t);
        if (mapped) {
          for (const c of mapped) requiredCustomerTags.push(c);
        }
      }
    }

    if (!requiredCustomerTags.length) return true; // produit public

    if (!Array.isArray(customerTags) || !customerTags.length) return false;
    const clientSet = new Set(
      customerTags.map((t) => String(t || '').trim().toLowerCase()).filter(Boolean)
    );
    return requiredCustomerTags.some((t) => clientSet.has(t));
  }

  /**
   * Calcule le total HT d'une liste de lignes ({variantId, quantity}) en
   * utilisant le map des variantes.
   * Les variantes inconnues sont ignorées (pas d'erreur — c'est juste
   * un calcul d'affichage côté client).
   */
  function computeTotalCents(lines, variantsById) {
    let sum = 0;
    for (const line of lines) {
      const v = variantsById.get(Number(line.variantId));
      if (!v) continue;
      const price = Number(v.priceCents) || 0;
      const qty = Number(line.quantity) || 0;
      if (qty > 0) sum += price * qty;
    }
    return sum;
  }

  /**
   * Convertit un Map<variantId, qty> en tableau de lignes.
   * Retire les quantités <= 0.
   */
  function selectionToLines(selection) {
    const out = [];
    for (const [variantId, qty] of selection) {
      const q = Number(qty) || 0;
      if (q > 0) out.push({ variantId: Number(variantId), quantity: q });
    }
    return out;
  }

  /**
   * Panier Shopify (`cart.items` de `/cart.js`) → sélection de l'étape 1
   * du module de réassort (variante → quantité).
   *
   * • Une même variante peut occuper plusieurs lignes du panier
   *   (propriétés différentes, découpage d'une appli) : les quantités
   *   s'additionnent, plafonnées à 999 comme partout dans le module.
   * • Une ligne remise à 0 alors que le produit a un prix (cadeau d'une
   *   appli, échantillon offert) est ignorée sans le dire : la reprendre
   *   la ferait payer au prix du catalogue. Le module a ses propres
   *   cadeaux. Ces lignes sont seulement comptées (`skipped`).
   * • `classify({ variantId, quantity, title })` tranche le reste :
   *   'ok' → reprise ; toute autre chaîne → refusée, avec cette raison
   *   (une seule fois par variante).
   *
   * @returns {{ selection: Map<number, number>, refused: Array<{variantId: number, title: string, reason: string}>, skipped: number }}
   */
  function planCartImport(items, classify) {
    const selection = new Map();
    const refused = [];
    const refusedIds = new Set();
    let skipped = 0;
    for (const raw of Array.isArray(items) ? items : []) {
      if (!raw || typeof raw !== 'object') continue;
      const variantId = Number(raw.variant_id != null ? raw.variant_id : raw.id);
      const quantity = Math.floor(Number(raw.quantity));
      if (!Number.isInteger(variantId) || variantId <= 0 || !(quantity > 0)) continue;
      if (Number(raw.final_line_price) === 0 && Number(raw.original_line_price) > 0) {
        skipped += 1;
        continue;
      }
      const item = { variantId, quantity, title: String(raw.title || raw.product_title || '').slice(0, 200) };
      const verdict = classify(item);
      if (verdict !== 'ok') {
        if (!refusedIds.has(variantId)) {
          refusedIds.add(variantId);
          refused.push({ variantId, title: item.title, reason: String(verdict) });
        }
        continue;
      }
      selection.set(variantId, Math.min(999, (selection.get(variantId) || 0) + quantity));
    }
    return { selection, refused, skipped };
  }

  /**
   * Modèle de données du panier, pour le tiroir de visualisation.
   *
   * Prend les groupes du parcours (`{ key, title, selection, offered }`,
   * une entrée par étape) et le catalogue indexé, et renvoie une structure
   * prête à rendre : les lignes enrichies (titres, prix unitaire, total
   * ligne, photo) et le sous-total de chaque groupe, plus le nombre total
   * d'articles pour la pastille du bouton flottant.
   *
   * `totalCents` ne compte que les groupes NON offerts (l'étape achat) :
   * c'est ce que la cliente paye, et la base des remises. Les groupes
   * offerts gardent leur `subtotalCents` — à charge de la vue d'en faire
   * ce qu'elle veut ; le tiroir, lui, ne l'affiche pas (prix barrés).
   *
   * Les variantes absentes de `variantsById` (catalogue pas encore chargé,
   * ou produit retiré de la boutique depuis la sauvegarde localStorage)
   * sont ignorées : impossible de les nommer ni de les valoriser. C'est la
   * même règle que `computeTotalCents`, donc les sous-totaux du tiroir
   * restent cohérents avec ceux de la summary bar.
   */
  function buildCartGroups(groups, variantsById) {
    const out = [];
    let itemCount = 0;
    let totalCents = 0;

    for (const group of groups || []) {
      const lines = [];
      let subtotalCents = 0;

      for (const line of selectionToLines(group.selection || new Map())) {
        const v = variantsById.get(Number(line.variantId));
        if (!v) continue;
        const quantity = Number(line.quantity) || 0;
        const unitCents = Number(v.priceCents) || 0;
        const lineCents = unitCents * quantity;
        subtotalCents += lineCents;
        itemCount += quantity;
        lines.push({
          variantId: Number(line.variantId),
          quantity,
          unitCents,
          lineCents,
          productTitle: String(v.productTitle || ''),
          variantTitle: String(v.title || ''),
          // Photo de la variante si elle en a une (chaque senteur a la
          // sienne au catalogue), sinon celle du produit parent.
          image: String(v.image || v.productImage || ''),
        });
      }

      if (!group.offered) totalCents += subtotalCents;

      out.push({
        key: String(group.key || ''),
        title: String(group.title || ''),
        offered: !!group.offered,
        lines,
        subtotalCents,
      });
    }

    return { groups: out, itemCount, totalCents };
  }

  /**
   * Détermine si un montant total atteint le seuil minimum.
   * Tout en cents.
   */
  function meetsMinimum(totalCents, minHtEuros) {
    const min = Math.round((Number(minHtEuros) || 0) * 100);
    return totalCents >= min;
  }

  /**
   * Parse une grille de remise PAR PALIERS : `seuil=pct`, séparés par des
   * point-virgules, le seuil en euros.
   *
   *   200=15;400=20;1000=30
   *
   * Lit : « à partir de 200 € −15 %, à partir de 400 € −20 %, à partir de
   * 1000 € −30 % ». Contrairement à `parseDiscountRules` (distributeur), où
   * la PREMIÈRE règle qui matche gagne et où l'ordre de saisie fait loi, ici
   * c'est le palier le PLUS HAUT atteint qui gagne — l'ordre de saisie n'a
   * donc aucune importance, et on trie nous-mêmes pour s'en affranchir.
   *
   * Une entrée illisible (seuil non numérique, pourcentage hors 0-100) est
   * ignorée sans emporter le reste de la grille : une faute de frappe sur un
   * palier ne doit pas supprimer la remise de tous les autres.
   *
   * Retourne `[{ minCents, pct }]` trié par seuil DÉCROISSANT.
   */
  function parseTierDiscounts(raw) {
    const tiers = [];
    for (const chunk of String(raw || '').split(';')) {
      const entry = chunk.trim();
      if (!entry || entry.charAt(0) === '#') continue;
      const eq = entry.lastIndexOf('=');
      if (eq <= 0) continue;
      const seuil = Number(entry.slice(0, eq).trim().replace(',', '.'));
      const pct = Number(entry.slice(eq + 1).trim().replace(',', '.'));
      if (!Number.isFinite(seuil) || seuil < 0) continue;
      if (!Number.isFinite(pct) || pct <= 0 || pct > 100) continue;
      tiers.push({ minCents: Math.round(seuil * 100), pct });
    }
    return tiers.sort((a, b) => b.minCents - a.minCents);
  }

  /**
   * Pourcentage de remise applicable à un total, selon la grille de paliers.
   * 0 si aucun palier n'est atteint (ou grille vide → mécanique inactive).
   *
   * Le seuil est INCLUSIF : à 400,00 € pile, c'est le palier 400 qui
   * s'applique, pas celui d'en dessous. C'est la lecture naturelle de « à
   * partir de 400 € », et c'est ce qu'attend une cliente qui a composé son
   * panier pour atteindre le palier au centime près.
   */
  function tierDiscountPct(totalCents, tiers) {
    const total = Number(totalCents) || 0;
    if (total <= 0 || !tiers || !tiers.length) return 0;
    for (const t of tiers) {
      if (total >= t.minCents) return t.pct;
    }
    return 0;
  }

  /**
   * Palier SUIVANT, pour afficher « encore X € pour passer à −20 % ».
   * null quand la cliente est déjà au palier le plus haut, ou quand la
   * grille est vide.
   */
  function nextTierDiscount(totalCents, tiers) {
    const total = Number(totalCents) || 0;
    if (!tiers || !tiers.length) return null;
    // Trié décroissant : le prochain palier est le dernier qu'on dépasse pas.
    let next = null;
    for (const t of tiers) {
      if (total < t.minCents) next = t;
    }
    if (!next) return null;
    return { minCents: next.minCents, pct: next.pct, missingCents: next.minCents - total };
  }

  /**
   * Calcule le budget alloué (en cents) à partir d'un pourcentage et d'un
   * montant HT en cents. Renvoie un entier (cents).
   */
  function computeBudgetCents(purchaseHtCents, pct) {
    const p = Math.max(0, Math.min(100, Number(pct) || 0));
    return Math.floor((Number(purchaseHtCents) || 0) * p / 100);
  }

  /**
   * Calcule le HT après remises clientes cumulées. Chaque remise est un
   * pourcentage appliqué INDÉPENDAMMENT sur la base purchaseHt (parallèle,
   * pas en cascade — c'est ce que fait Shopify au checkout). Le total
   * remisé est la somme des montants arrondis individuellement.
   *
   * Utilisé côté réassort pour calculer les budgets PLV/cadeaux sur le
   * net après remises, pas sur le brut (= moins de PLV/cadeaux pour les
   * cohortes les plus remisées, ce qui est la règle métier voulue).
   *
   * `discounts` : array d'objets `{ percentage: number }`. Tout autre champ
   * est ignoré. Pourcentage absent ou ≤ 0 → ignoré.
   *
   * Si pas de remises → renvoie purchaseHtCents tel quel.
   */
  function computeDiscountedHt(purchaseHtCents, discounts) {
    const base = Number(purchaseHtCents) || 0;
    if (!base || !Array.isArray(discounts) || !discounts.length) return base;
    let totalDiscount = 0;
    for (const d of discounts) {
      const pct = Number(d?.percentage);
      if (!Number.isFinite(pct) || pct <= 0) continue;
      totalDiscount += Math.round(base * pct / 100);
    }
    return Math.max(0, base - totalDiscount);
  }

  /**
   * Détermine si la quantité demandée dépasse le budget restant.
   * Utilisée pour bloquer les "+1" qui passeraient au-dessus.
   */
  function exceedsBudget(currentCents, addCents, budgetCents) {
    return (Number(currentCents) || 0) + (Number(addCents) || 0) > (Number(budgetCents) || 0);
  }

  /**
   * Format euros français (depuis cents).
   */
  function formatEuros(cents) {
    const n = (Number(cents) || 0) / 100;
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
    }).format(n);
  }

  /**
   * Échappe HTML pour insertion sûre dans innerHTML.
   */
  function escapeHtml(str) {
    return String(str ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /**
   * Parse la grille de remises distributeur.
   *
   * MIROIR EXACT de `parseDiscountRules` dans `server/src/lib/validate.js` :
   * le front affiche les prix remisés, le serveur fait foi sur le devis. Si
   * les deux divergent, la distributrice voit un prix et en paye un autre.
   * Toute évolution ici doit être reportée là-bas (invariant #1 de CLAUDE.md).
   *
   * Format (règles séparées par `;`, ÉVALUÉES DANS L'ORDRE, 1re qui matche
   * gagne) : `tag=pct`, `tagA|tagB=pct`, `@fragment-de-titre=pct`, `*=pct`.
   */
  function parseDiscountRules(raw) {
    const rules = [];
    for (const chunk of String(raw || '').split(';')) {
      const entry = chunk.trim();
      if (!entry) continue;
      const eq = entry.lastIndexOf('=');
      if (eq <= 0) continue;
      const pct = Number(entry.slice(eq + 1).trim());
      if (!Number.isFinite(pct) || pct < 0 || pct > 100) continue;
      const key = entry.slice(0, eq).trim();
      if (key === '*') { rules.push({ fallback: true, pct }); continue; }
      if (key.charAt(0) === '@') {
        rules.push({ titleFragment: key.slice(1).trim().toLowerCase(), pct });
        continue;
      }
      const tags = key.split('|').map(function (t) { return t.trim().toLowerCase(); }).filter(Boolean);
      if (tags.length) rules.push({ tags: tags, pct: pct });
    }
    return rules;
  }

  /**
   * Taux de remise distributeur d'un produit, en %. 0 si aucune règle ne
   * matche (grille vide sur les flows pro → fonctionnalité inactive).
   */
  function discountPctForProduct(product, rules) {
    if (!product || !rules || !rules.length) return 0;
    const title = String(product.title || '').toLowerCase();
    const tags = (product.tags || []).map(function (t) { return String(t).toLowerCase(); });
    for (const r of rules) {
      if (r.fallback) return r.pct;
      if (r.titleFragment) {
        if (title.indexOf(r.titleFragment) !== -1) return r.pct;
        continue;
      }
      if (r.tags.some(function (t) { return tags.indexOf(t) !== -1; })) return r.pct;
    }
    return 0;
  }

  /**
   * Remplit les jetons {nom} d'un libellé réglé dans la section.
   *
   * Ces libellés sont traduits par la traduction automatique de la
   * boutique. Si elle a perdu ou traduit un jeton obligatoire (« {importo} »
   * au lieu de « {amount} »), on renvoie null : l'appelant garde alors son
   * libellé par défaut, plutôt que d'afficher un jeton brut ou d'omettre le
   * montant. Un jeton inconnu reste tel quel.
   *
   * @param {string} template — ex. "Encore {amount} {tax} pour valider"
   * @param {Object} values — ex. { amount: "300,00 €", tax: "HT" }
   * @param {string[]} [required] — jetons qui doivent figurer dans le modèle
   * @returns {string|null}
   */
  function fillPlaceholders(template, values, required) {
    const tpl = typeof template === 'string' ? template.trim() : '';
    if (!tpl) return null;
    const vals = values || {};
    const missing = (required || []).some(function (k) { return tpl.indexOf('{' + k + '}') === -1; });
    if (missing) return null;
    return tpl.replace(/\{(\w+)\}/g, function (token, k) {
      return Object.prototype.hasOwnProperty.call(vals, k) ? String(vals[k]) : token;
    });
  }

  /* ── Langues (dictionnaire : prodevis-i18n.js) ───────────────────────
   *
   * Le français n'est pas dans le dictionnaire : il reste dans le code,
   * en littéral de repli. Langue française ou inconnue, fichier de
   * traductions absent, langue ou clé manquante → le littéral français,
   * à l'octet près. `dict` est `window.ProDevisI18n`, éventuellement
   * absent : chaque fonction ci-dessous le tolère. */

  /**
   * Code de langue principal, en minuscules : « pt-PT » → « pt »,
   * « EN » → « en ». Chaîne vide si la valeur est illisible.
   */
  function normalizeLocale(raw) {
    var code = String(raw == null ? '' : raw).trim().toLowerCase().split(/[-_]/)[0];
    return /^[a-z]{2,3}$/.test(code) ? code : '';
  }

  /**
   * Forme plurielle d'un message : « one » ou « other ».
   *
   * Le français met le singulier à 0 et 1 : c'est la règle des messages
   * historiques (`n > 1 ? 's' : ''`). Les langues du dictionnaire (en, de,
   * es, it, nl, pt) ne le mettent qu'à 1, sur des quantités entières.
   */
  function pluralCategory(lang, count) {
    var n = Math.abs(Number(count) || 0);
    if (lang === 'fr') return n < 2 ? 'one' : 'other';
    return n === 1 ? 'one' : 'other';
  }

  /** Espaces et retours à la ligne ramenés à une espace, bords coupés. */
  function normalizeText(text) {
    return String(text == null ? '' : text).replace(/\s+/g, ' ').trim();
  }

  /** Vrai si la page se traduit : langue connue, autre que le français. */
  function isTranslated(dict, lang) {
    return !!(lang && lang !== 'fr' && dict && dict.languages && dict.languages.indexOf(lang) !== -1);
  }

  /**
   * Traduction brute d'un message (modèle non rempli), ou null.
   * Chaîne, ou objet { one, other } pour un message au pluriel.
   */
  function lookupMessage(dict, lang, key) {
    if (!isTranslated(dict, lang) || !dict.messages) return null;
    var entry = Object.prototype.hasOwnProperty.call(dict.messages, key) ? dict.messages[key] : null;
    var value = entry && entry[lang];
    if (typeof value === 'string') return value.trim() ? value : null;
    if (value && typeof value === 'object' && typeof value.other === 'string') return value;
    return null;
  }

  /**
   * Message du bundle dans la langue de la page.
   *
   * @param {Object} dict — window.ProDevisI18n (peut manquer)
   * @param {string} lang — code normalisé (normalizeLocale)
   * @param {string} key — id stable du message
   * @param {string|Object} fr — littéral français, ou { one, other }
   * @param {Object} [vars] — valeurs des jetons {nom} ; `count` choisit
   *   la forme plurielle
   */
  function translateMessage(dict, lang, key, fr, vars) {
    var translated = lookupMessage(dict, lang, key);
    var tpl = translated || fr;
    if (tpl && typeof tpl === 'object') {
      var form = pluralCategory(translated ? lang : 'fr', vars && vars.count);
      tpl = tpl[form] || tpl.other;
    }
    return fillPlaceholders(tpl, vars) || String(tpl == null ? '' : tpl);
  }

  /**
   * Texte d'un réglage de section affiché tel quel (libellé, titre…).
   *
   * Une valeur non vide l'emporte : elle porte le vocabulaire du flow
   * (« commande » et non « devis ») ou une traduction faite dans Shopify.
   * Sauf, hors français, si elle vaut exactement un défaut français
   * connu — le littéral `fr` du bundle, ou une valeur de `dict.sources`
   * (défauts des sections, valeurs posées par leur Liquid) : réglage
   * jamais traduit, on prend le dictionnaire. Vide → `translateMessage`.
   *
   * Renvoie le MODÈLE, jetons non remplis : un appelant qui en a (le
   * libellé « Encore {amount} {tax}… ») le passe à fillPlaceholders.
   *
   * @param {*} value — valeur reçue de la section (non transformée en
   *   français : renvoyée telle quelle, à l'octet près)
   * @param {string} [key] — id du message qui traduit le littéral `fr`
   * @param {string} [fr] — défaut français du bundle
   */
  function resolveSettingText(dict, lang, value, key, fr) {
    var v = value == null ? '' : String(value);
    if (!isTranslated(dict, lang)) return v;
    var norm = normalizeText(v);
    if (!norm) return key ? translateMessage(dict, lang, key, fr) : v;
    var id = typeof fr === 'string' && norm === normalizeText(fr)
      ? key
      : (dict.sources && Object.prototype.hasOwnProperty.call(dict.sources, norm) ? dict.sources[norm] : null);
    var translated = id ? lookupMessage(dict, lang, id) : null;
    return typeof translated === 'string' ? translated : v;
  }

  /**
   * Nom d'onglet du catalogue (catégorie ou groupe), À L'AFFICHAGE
   * seulement : la correspondance avec les tags se fait toujours sur le
   * libellé français de « Catégories groupées ». Libellé inconnu → tel
   * quel. Casse et espaces tolérés (« Huiles de massage »).
   */
  function translateLabel(dict, lang, label) {
    var text = String(label == null ? '' : label);
    if (!isTranslated(dict, lang) || !dict.labels) return text;
    var norm = normalizeText(text);
    var entry = Object.prototype.hasOwnProperty.call(dict.labels, norm) ? dict.labels[norm] : null;
    if (!entry) {
      var lower = norm.toLowerCase();
      for (var k in dict.labels) {
        if (Object.prototype.hasOwnProperty.call(dict.labels, k) && k.toLowerCase() === lower) {
          entry = dict.labels[k];
          break;
        }
      }
    }
    var translated = entry && entry[lang];
    return typeof translated === 'string' && translated.trim() ? translated : text;
  }

  /* ── Noms de produits et de variantes, à l'affichage ─────────────────
   *
   * Le catalogue se lit en français, à la racine (fetchCollection de
   * prodevis.js) : tags, intitulés et regex des réglages s'y accordent.
   * Hors français, les NOMS affichés viennent des traductions enregistrées
   * dans Shopify (la même collection lue sous « /en/ », « /de/ »…), passées
   * aux deux garde-fous ci-dessous. Le français n'y passe jamais : sans
   * traduction, le nom reste celui du catalogue, à l'octet près. */

  /**
   * Racine de la langue de la page (`window.Shopify.routes.root`) quand ce
   * n'est pas la langue principale : « /en/ », « /pt-pt/ »… Chaîne vide
   * pour « / » ou pour une valeur inattendue (pas de requête alors).
   */
  function catalogLocaleRoot(root) {
    var r = typeof root === 'string' ? root.trim() : '';
    return /^\/[a-z]{2,3}(?:-[a-z0-9]{2,8})*\/$/i.test(r) ? r : '';
  }

  /** Nombres d'un texte, triés ; « 19,90 » vaut « 19.90 ». */
  function numbersOf(text) {
    var found = String(text == null ? '' : text).match(/\d+(?:[.,]\d+)?/g) || [];
    return found.map(function (n) { return n.replace(',', '.'); }).sort();
  }

  /**
   * Nom traduit par Shopify s'il est sûr, sinon le nom français.
   *
   * Une traduction enregistrée ne suit pas le titre français quand on le
   * modifie : en anglais, « Revente 150 ml » était resté « Resale 125 ml »
   * et « Chevalets A4 » s'affichait « A5 easels ». Elle n'est donc gardée
   * que si elle porte les MÊMES nombres que le français (contenances,
   * quantités, formats, années) : une traduction périmée qui changerait un
   * chiffre affiché ne passe pas.
   *
   * @param {string} fr — nom du catalogue français
   * @param {string} [translated] — nom lu sous la racine de la langue
   */
  function pickTranslatedTitle(fr, translated) {
    var base = String(fr == null ? '' : fr);
    var tr = typeof translated === 'string' ? translated.trim() : '';
    if (!tr || tr === base) return base;
    var a = numbersOf(base);
    var b = numbersOf(tr);
    if (a.length !== b.length) return base;
    for (var i = 0; i < a.length; i++) {
      if (a[i] !== b[i]) return base;
    }
    return tr;
  }

  // Lettres et chiffres : ce qui colle à un mot. Une entrée de `formats`
  // ne remplace qu'un mot entier (« Offert », pas le début d'« Offerts »).
  var WORD_CHARS = '0-9A-Za-zÀ-ÖØ-öø-ÿ';

  /**
   * Motif français d'une entrée de `formats` → expression régulière. Un
   * jeton {nom} y vaut un nombre (« 10 », « 39.90 ») ; une espace, zéro ou
   * plusieurs (« 10 % » comme « 10% »). Groupe 1 : ce qui précède le mot.
   */
  function compileFormatTerm(fr) {
    var src = String(fr == null ? '' : fr).trim();
    var names = [];
    var body = src.split(/(\{\w+\})/).map(function (part) {
      var token = /^\{(\w+)\}$/.exec(part);
      if (token) {
        names.push(token[1]);
        return '(\\d+(?:[.,]\\d+)?)';
      }
      return part.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/\s+/g, '\\s*');
    }).join('');
    var start = new RegExp('^[' + WORD_CHARS + '{]').test(src) ? '(^|[^' + WORD_CHARS + '])' : '()';
    var end = new RegExp('[' + WORD_CHARS + '}]$').test(src) ? '(?=$|[^' + WORD_CHARS + '])' : '';
    return { re: new RegExp(start + body + end, 'gi'), names: names };
  }

  /**
   * Intitulé d'une variante « format » (Voyage, Revente, Cabine…) traduit
   * par le dictionnaire (`dict.formats`), ou null si ce n'en est pas un.
   *
   * Les traductions que Shopify a enregistrées pour ces intitulés étaient
   * fausses (« Booth 1 L », « Skab 1 L » — une armoire — pour « Cabine
   * 1 L ») ou périmées : ici on traduit les mots connus, les nombres et le
   * reste de l'intitulé restent ceux du français. Seulement pour les
   * intitulés dont le premier mot est dans `dict.formats.lead` ; les
   * autres (senteurs, lots…) passent par pickTranslatedTitle.
   */
  function translateFormatTitle(dict, lang, title) {
    var text = String(title == null ? '' : title);
    if (!isTranslated(dict, lang) || !dict.formats) return null;
    var first = text.trim().split(/\s+/)[0].toLowerCase();
    var isFormat = (dict.formats.lead || []).some(function (w) { return String(w).toLowerCase() === first; });
    if (!isFormat) return null;
    // Chaque remplacement est mis de côté derrière une marque (zone à usage
    // privé d'Unicode, ni lettre ni chiffre) : une entrée suivante ne peut
    // pas retraduire le texte qu'une autre vient de poser.
    var done = [];
    var out = text;
    (dict.formats.terms || []).forEach(function (entry) {
      var tr = entry && entry[1] && entry[1][lang];
      if (typeof tr !== 'string' || !tr.trim()) return;
      var term = compileFormatTerm(entry[0]);
      out = out.replace(term.re, function () {
        var args = arguments;
        var vals = {};
        term.names.forEach(function (name, i) { vals[name] = args[i + 2]; });
        done.push(fillPlaceholders(tr, vals));
        return args[1] + '' + String.fromCharCode(0xE100 + done.length - 1);
      });
    });
    return out.replace(/([-])/g, function (mark, code) {
      return done[code.charCodeAt(0) - 0xE100];
    });
  }

  return {
    parseDiscountRules,
    discountPctForProduct,
    compileWhitelistRegex,
    isSafeHttpUrl,
    filterVariants,
    hasFormatVariant,
    parseHeatExceptions,
    isHeatBlockedVariant,
    hasAnyTag,
    isProductCustomerEligible,
    parseCustomerTagMap,
    classifyVariantStock,
    computeTotalCents,
    selectionToLines,
    planCartImport,
    buildCartGroups,
    parseTierDiscounts,
    tierDiscountPct,
    nextTierDiscount,
    meetsMinimum,
    computeBudgetCents,
    computeDiscountedHt,
    exceedsBudget,
    formatEuros,
    escapeHtml,
    fillPlaceholders,
    normalizeLocale,
    pluralCategory,
    normalizeText,
    isTranslated,
    lookupMessage,
    translateMessage,
    resolveSettingText,
    translateLabel,
    catalogLocaleRoot,
    pickTranslatedTitle,
    translateFormatTitle,
  };
});